import { useQuery } from '@apollo/client';
import { Download, ExternalLink, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/shared/ui/button';
import { showToast } from '@/shared/ui/toast';

import { downloadAttachment } from './attachments-rest';
import { ClinicalVisitStatusBadge } from './ClinicalVisitStatusBadge';
import {
  CLINICAL_ATTACHMENTS_QUERY,
  CLINICAL_IMAGING_STUDIES_QUERY,
  CLINICAL_LAB_REQUESTS_QUERY,
  CLINICAL_PRESCRIPTIONS_QUERY,
  CLINICAL_PRESCRIPTION_LINES_QUERY,
  CLINICAL_RECORD_QUERY,
  CLINICAL_VISIT_DETAIL_QUERY,
  PET_CLINICAL_SUMMARY_QUERY,
  PET_HOSPITALIZATIONS_QUERY,
  PET_VITALS_HISTORY_QUERY,
} from './clinical.gql';
import {
  buildClinicalRecordHtml,
  printClinicalRecord,
  type ClinicalRecordPrintPatient,
} from './clinical-record-print';
import { printImagingOrder } from './imaging-order-print';
import { printLabOrder } from './lab-order-print';
import { openPrescriptionPrint, RX_PRINTABLE_STATUSES, type RxPrintBase } from './PrescriptionsSection';
import { formatWeightLabel, useRxPrintBase } from './useRxPrintBase';
import { WeightChart } from './WeightChart';

/** Read-only prescription card for the lifetime history — shows each drug line + reprint. */
function HistoryPrescriptionCard({
  rx,
  printBase,
}: {
  rx: any;
  printBase: RxPrintBase;
}): JSX.Element {
  const { t } = useTranslation();
  const { data } = useQuery(CLINICAL_PRESCRIPTION_LINES_QUERY, {
    variables: { prescription_id: rx.id },
    fetchPolicy: 'cache-and-network',
  });
  const lines: any[] = data?.clinicalPrescriptionLines ?? [];

  return (
    <div className="space-y-1 rounded-md border p-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {t('clinical.prescription')} ·{' '}
          <span className="text-muted-foreground">{t(`clinical.rx_status_${rx.status}`)}</span>
        </span>
        {RX_PRINTABLE_STATUSES.has(rx.status) ? (
          <Button size="sm" variant="outline" onClick={() => void openPrescriptionPrint(printBase, rx, lines, t)}>
            {t('clinical.rx_print')}
          </Button>
        ) : null}
      </div>
      {lines.length === 0 ? (
        <p className="text-muted-foreground text-xs">{t('clinical.no_rx_lines')}</p>
      ) : (
        <ul className="space-y-0.5 text-sm">
          {lines.map((l) => (
            <li key={l.id}>
              {l.is_controlled ? <span className="text-destructive">▲ </span> : null}
              <strong>{l.drug_name}</strong>
              {l.dose ? ` · ${l.dose}${l.dose_unit ? ' ' + l.dose_unit : ''}` : ''}
              {` · ${t(`clinical.route_${l.route}`)}`}
              {l.frequency ? ` · ${l.frequency}` : ''}
              {l.duration_days ? ` · ${l.duration_days}d` : ''}
              {l.instructions ? <span className="text-muted-foreground"> — {l.instructions}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PetTrends({ petId }: { petId: string }): JSX.Element | null {
  const { t } = useTranslation();
  const { data } = useQuery(PET_VITALS_HISTORY_QUERY, {
    variables: { pet_id: petId },
    skip: !petId,
    fetchPolicy: 'cache-and-network',
  });
  const points: any[] = data?.petVitalsHistory ?? [];
  if (points.length === 0) return null;
  const weightPoints = points.filter((p) => p.weight_grams != null);
  const tempC = (tenths: number | null): string => (tenths == null ? '—' : (tenths / 10).toFixed(1));
  const kg = (g: number | null): string => (g == null ? '—' : (g / 1000).toFixed(2));

  return (
    <div className="border-b px-4 py-3">
      <h3 className="mb-1 text-sm font-semibold">{t('clinical.trends_title')}</h3>
      {weightPoints.length >= 2 ? <WeightChart points={weightPoints} /> : null}
      <div className="mt-2 max-h-32 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="text-muted-foreground">
            <tr className="text-left">
              <th className="pr-2 font-medium">{t('clinical.th_date')}</th>
              <th className="pr-2 font-medium">{t('clinical.th_weight_kg')}</th>
              <th className="pr-2 font-medium">°C</th>
              <th className="pr-2 font-medium">FC</th>
              <th className="pr-2 font-medium">FR</th>
              <th className="font-medium">CC</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p) => (
              <tr key={p.visit_id} className="border-t">
                <td className="pr-2">{new Date(p.measured_at).toLocaleDateString()}</td>
                <td className="pr-2">{kg(p.weight_grams)} kg</td>
                <td className="pr-2">{tempC(p.temperature_celsius_tenths)}</td>
                <td className="pr-2">{p.heart_rate_bpm ?? '—'}</td>
                <td className="pr-2">{p.respiratory_rate_bpm ?? '—'}</td>
                <td>{p.body_condition_score ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VisitExtras({
  visitId,
  printBase,
  recordPatient,
  chiefComplaint,
  primaryDiagnosis,
}: {
  visitId: string;
  printBase: RxPrintBase;
  recordPatient: ClinicalRecordPrintPatient;
  chiefComplaint?: string | null;
  primaryDiagnosis?: string | null;
}): JSX.Element | null {
  const { t } = useTranslation();
  const { data: aData } = useQuery(CLINICAL_ATTACHMENTS_QUERY, {
    variables: { visit_id: visitId },
    fetchPolicy: 'cache-and-network',
  });
  const { data: lData } = useQuery(CLINICAL_LAB_REQUESTS_QUERY, {
    variables: { visit_id: visitId },
    fetchPolicy: 'cache-and-network',
  });
  const { data: iData } = useQuery(CLINICAL_IMAGING_STUDIES_QUERY, {
    variables: { visit_id: visitId },
    fetchPolicy: 'cache-and-network',
  });
  const { data: rxData } = useQuery(CLINICAL_PRESCRIPTIONS_QUERY, {
    variables: { visit_id: visitId },
    fetchPolicy: 'cache-and-network',
  });
  const attachments: any[] = aData?.clinicalAttachments ?? [];
  const labs: any[] = lData?.clinicalLabRequests ?? [];
  const imaging: any[] = iData?.clinicalImagingStudies ?? [];
  const prescriptions: any[] = rxData?.clinicalPrescriptions ?? [];
  if (
    attachments.length === 0 &&
    labs.length === 0 &&
    imaging.length === 0 &&
    prescriptions.length === 0
  )
    return null;

  return (
    <>
      {prescriptions.length > 0 ? (
        <section className="space-y-2">
          <h3 className="font-semibold">{t('clinical.tab_prescriptions')}</h3>
          {prescriptions.map((p) => (
            <HistoryPrescriptionCard key={p.id} rx={p} printBase={printBase} />
          ))}
        </section>
      ) : null}
      {labs.length > 0 ? (
        <section className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold">{t('clinical.tab_labs')}</h3>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                void (async () => {
                  const ok = await printLabOrder(
                    { patient: recordPatient, chief_complaint: chiefComplaint, primary_diagnosis: primaryDiagnosis, requests: labs },
                    t,
                  );
                  if (!ok) showToast({ tone: 'error', title: t('clinical.record_print_blocked') });
                })()
              }
            >
              <Download className="h-4 w-4" aria-hidden />
              <span>{t('clinical.download_lab_order')}</span>
            </Button>
          </div>
          <ul className="text-sm">
            {labs.map((l) => (
              <li key={l.id}>
                {l.panel_name || l.lab_name || t('clinical.lab_request')} ·{' '}
                <span className="text-muted-foreground">{t(`clinical.lab_status_${l.status}`)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {imaging.length > 0 ? (
        <section className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold">{t('clinical.tab_imaging')}</h3>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                void (async () => {
                  const ok = await printImagingOrder(
                    { patient: recordPatient, chief_complaint: chiefComplaint, primary_diagnosis: primaryDiagnosis, studies: imaging },
                    t,
                  );
                  if (!ok) showToast({ tone: 'error', title: t('clinical.record_print_blocked') });
                })()
              }
            >
              <Download className="h-4 w-4" aria-hidden />
              <span>{t('clinical.download_imaging_order')}</span>
            </Button>
          </div>
          <ul className="text-sm">
            {imaging.map((s) => (
              <li key={s.id}>
                <strong>{t(`clinical.study_kind_${s.study_kind}`)}</strong>
                {s.body_region ? ` · ${s.body_region}` : ''}
                {s.interpretation ? <span className="text-muted-foreground"> — {s.interpretation}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {attachments.length > 0 ? (
        <section className="space-y-1">
          <h3 className="font-semibold">{t('clinical.tab_attachments')}</h3>
          <ul className="text-sm">
            {attachments.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-2">
                <span>
                  {t(`clinical.attachment_kind_${a.kind}`)} · {a.title || a.original_filename}
                </span>
                <Button size="sm" variant="outline" onClick={() => void downloadAttachment(a.id, a.original_filename)}>
                  {t('clinical.download')}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */

function Row({ label, value }: { label: string; value: string | number | null }): JSX.Element | null {
  if (value == null || value === '') return null;
  return (
    <p className="text-sm">
      <span className="text-muted-foreground">{label}: </span>
      {value}
    </p>
  );
}

/**
 * Read-only hospitalization timeline for the pet. Hospitalization is a SEPARATE care/billing
 * episode (not a SOAP visit), surfaced here purely for visibility — each row deep-links into the
 * hospitalization board with the episode pre-selected so the vet can see the actual
 * evolution/vitals/charges. Hidden when the pet has no stays.
 */
function HospitalizationsSection({
  petId,
  onClose,
}: {
  petId: string;
  onClose: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useQuery(PET_HOSPITALIZATIONS_QUERY, {
    variables: { pet_id: petId },
    skip: !petId,
    fetchPolicy: 'cache-and-network',
  });
  const episodes: any[] = data?.hospitalizationEpisodes ?? [];
  if (episodes.length === 0) return null;
  const goToEpisode = (id: string): void => {
    onClose();
    navigate(`/app/hospitalization?episode=${id}`);
  };
  return (
    <div className="border-b px-4 py-2">
      <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
        {t('clinical.hospitalizations_title')}
      </p>
      <ul className="mt-1 space-y-1">
        {episodes.map((e) => (
          <li key={e.id}>
            <button
              type="button"
              onClick={() => goToEpisode(e.id)}
              className="hover:bg-muted flex w-full items-center justify-between gap-2 rounded-md border p-2 text-left text-sm"
            >
              <span className="min-w-0">
                <span className="font-mono">{e.folio_number}</span>
                <span className="text-muted-foreground">
                  {' · '}
                  {t(`hospitalization.status_${e.status}`, { defaultValue: e.status })}
                  {' · '}
                  {new Date(e.admitted_at).toLocaleDateString()}
                  {e.discharged_at ? ` → ${new Date(e.discharged_at).toLocaleDateString()}` : ''}
                </span>
                {e.admission_reason ? (
                  <span className="text-muted-foreground block truncate text-xs">
                    {e.admission_reason}
                  </span>
                ) : null}
              </span>
              <span className="text-primary flex items-center gap-1 whitespace-nowrap text-xs">
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                {t('clinical.view_hospitalization')}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Read-only lifetime record: every visit for the pet + each visit's sections/addenda. */
export function ClinicalHistoryModal({
  pet_id,
  onClose,
}: {
  pet_id: string;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: recordData, loading } = useQuery(CLINICAL_RECORD_QUERY, {
    variables: { pet_id },
    skip: !pet_id,
    fetchPolicy: 'cache-and-network',
  });
  const record = recordData?.clinicalRecord ?? null;
  const visits: any[] = record?.visits ?? [];

  const { data: petData } = useQuery(PET_CLINICAL_SUMMARY_QUERY, {
    variables: { pet_id },
    skip: !pet_id,
    fetchPolicy: 'cache-and-network',
  });
  const petInfo = petData?.petClinicalSummary ?? null;

  const { data: detailData } = useQuery(CLINICAL_VISIT_DETAIL_QUERY, {
    variables: { id: selectedId ?? '' },
    skip: !selectedId,
    fetchPolicy: 'cache-and-network',
  });
  const detail = detailData?.clinicalVisit ?? null;

  const rx_print_info = useRxPrintBase({
    pet_id,
    veterinarian_id: detail?.visit?.veterinarian_id ?? null,
    pet_name: petInfo?.pet_name ?? null,
    species: petInfo?.species ?? null,
    pet_record_code: petInfo?.pet_record_code ?? null,
  });
  const rx_weight_label = formatWeightLabel(
    detail?.physical_exam?.body_weight_grams ?? detail?.visit?.weight_grams_at_visit ?? null,
  );
  // Same "patient" header shape used by the expediente + órdenes de imagenología/laboratorio.
  const clinical_print_patient = {
    ...rx_print_info,
    weight_label: rx_weight_label,
    visit_date: detail?.visit ? new Date(detail.visit.created_at).toLocaleDateString() : '—',
    visit_folio: detail?.visit?.folio_number ?? '—',
  };
  const primary_diagnosis =
    (detail?.diagnoses ?? []).find((d: any) => d.is_primary)?.description ?? null;

  const buildPrintInput = () => ({
    patient: clinical_print_patient,
    anamnesis: detail?.anamnesis ?? null,
    exam: detail?.physical_exam ?? null,
    diagnoses: detail?.diagnoses ?? [],
    plan: detail?.plan_treatment ?? null,
    addendums: detail?.addendums ?? [],
  });

  const onDownloadRecord = async (): Promise<void> => {
    if (!detail?.visit) return;
    const ok = await printClinicalRecord(buildPrintInput(), t);
    if (!ok) showToast({ tone: 'error', title: t('clinical.record_print_blocked') });
  };

  // Inline preview ("ver sin descargar") — same document `onDownloadRecord` would print,
  // rendered directly into an iframe instead. Regenerated whenever the selected visit's
  // detail finishes loading; `cancelled` guards against a stale response landing after the
  // vet has already clicked a different visit.
  const [preview_html, setPreviewHtml] = useState<string | null>(null);
  const [preview_loading, setPreviewLoading] = useState(false);
  useEffect(() => {
    if (!detail?.visit) {
      setPreviewHtml(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    void buildClinicalRecordHtml(buildPrintInput(), t).then((html) => {
      if (!cancelled) {
        setPreviewHtml(html);
        setPreviewLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // Keyed on the visit id on purpose — rebuilding on every keystroke of unrelated state
    // isn't needed for a read-only history view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.visit?.id]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-background flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg border shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-base font-semibold">
            {t('clinical.history_title')}
            {record ? ` · ${record.code}` : ''}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('common.cancel')}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <PetTrends petId={pet_id} />

        <HospitalizationsSection petId={pet_id} onClose={onClose} />

        <div className="grid flex-1 grid-cols-[260px_1fr] overflow-hidden">
          {/* Visit list */}
          <div className="overflow-y-auto border-r p-2">
            {loading && visits.length === 0 ? (
              <p className="text-muted-foreground p-2 text-sm">{t('common.loading')}</p>
            ) : visits.length === 0 ? (
              <p className="text-muted-foreground p-2 text-sm">{t('clinical.history_empty')}</p>
            ) : (
              <ul className="space-y-1">
                {visits.map((v) => (
                  <li key={v.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(v.id)}
                      className={`w-full rounded-md border p-2 text-left text-sm ${
                        selectedId === v.id ? 'border-primary bg-primary/5' : 'hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-medium">{v.folio_number}</span>
                        <ClinicalVisitStatusBadge status={v.status} />
                      </div>
                      <span className="text-muted-foreground text-xs">
                        {new Date(v.created_at).toLocaleDateString()}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Selected visit detail (read-only) */}
          <div className="overflow-y-auto p-4">
            {!detail ? (
              <p className="text-muted-foreground text-sm">{t('clinical.history_pick')}</p>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void onDownloadRecord()}
                  >
                    <Download className="h-4 w-4" aria-hidden />
                    <span>{t('clinical.download_record')}</span>
                  </Button>
                </div>
                <section className="space-y-1">
                  <h3 className="font-semibold">{t('clinical.tab_anamnesis')}</h3>
                  <Row label={t('clinical.f_chief_complaint')} value={detail.anamnesis?.chief_complaint ?? detail.visit?.chief_complaint ?? null} />
                  <Row label={t('clinical.f_previous_illnesses')} value={detail.anamnesis?.previous_illnesses ?? null} />
                  <Row label={t('clinical.f_current_medications')} value={detail.anamnesis?.current_medications ?? null} />
                  <Row label={t('clinical.f_other_notes')} value={detail.anamnesis?.other_notes ?? null} />
                </section>
                <section className="space-y-1">
                  <h3 className="font-semibold">{t('clinical.tab_exam')}</h3>
                  <Row label={t('clinical.f_body_weight_grams')} value={detail.physical_exam?.body_weight_grams ?? detail.visit?.weight_grams_at_visit ?? null} />
                  <Row label={t('clinical.f_body_temperature_celsius_tenths')} value={detail.physical_exam?.body_temperature_celsius_tenths ?? null} />
                  <Row label={t('clinical.f_general_impression')} value={detail.physical_exam?.general_impression ?? null} />
                </section>
                <section className="space-y-1">
                  <h3 className="font-semibold">{t('clinical.tab_diagnoses')}</h3>
                  {(detail.diagnoses ?? []).length === 0 ? (
                    <p className="text-muted-foreground text-sm">{t('clinical.no_diagnoses')}</p>
                  ) : (
                    <ul className="list-disc pl-5 text-sm">
                      {(detail.diagnoses as any[]).map((d) => (
                        <li key={d.id}>
                          {d.is_primary ? '★ ' : ''}
                          <strong>{t(`clinical.diagnosis_kind_${d.diagnosis_kind}`)}</strong>: {d.description}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
                <section className="space-y-1">
                  <h3 className="font-semibold">{t('clinical.tab_plan')}</h3>
                  <Row label={t('clinical.f_treatment_notes')} value={detail.plan_treatment?.treatment_notes ?? null} />
                  <Row label={t('clinical.f_medications_notes')} value={detail.plan_treatment?.medications_notes ?? null} />
                  <Row label={t('clinical.f_follow_up_notes')} value={detail.plan_treatment?.follow_up_notes ?? null} />
                </section>
                <section className="space-y-1">
                  <h3 className="font-semibold">{t('clinical.tab_addendums')}</h3>
                  {(detail.addendums ?? []).length === 0 ? (
                    <p className="text-muted-foreground text-sm">{t('clinical.no_addendums')}</p>
                  ) : (
                    <ul className="space-y-1">
                      {(detail.addendums as any[]).map((ad) => (
                        <li key={ad.id} className="rounded-md border px-2 py-1 text-sm">
                          <p className="text-muted-foreground text-xs">{new Date(ad.created_at).toLocaleString()}</p>
                          <p>{ad.content}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
                <VisitExtras
                  visitId={detail.visit.id}
                  printBase={{
                    ...rx_print_info,
                    visit_folio: detail.visit.folio_number ?? '—',
                    weight_label: rx_weight_label,
                  }}
                  recordPatient={clinical_print_patient}
                  chiefComplaint={detail.anamnesis?.chief_complaint ?? detail.visit.chief_complaint ?? null}
                  primaryDiagnosis={primary_diagnosis}
                />

                <section className="space-y-1">
                  <h3 className="font-semibold">{t('clinical.record_preview_title')}</h3>
                  {preview_loading && !preview_html ? (
                    <p className="text-muted-foreground text-sm">{t('clinical.record_preview_loading')}</p>
                  ) : preview_html ? (
                    <iframe
                      srcDoc={preview_html}
                      sandbox=""
                      title={t('clinical.record_preview_title')}
                      className="h-[70vh] w-full rounded-md border bg-white"
                    />
                  ) : null}
                </section>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
